import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
  type PanInfo,
} from "framer-motion";
import { useLayoutEffect, useRef, useState } from "react";
import DateTimeRelative from "~/components/DateTimeRelative";
import { formatSmartDate } from "~/lib/formatDate";
import type { Notification } from "~/types";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

const DISMISS_THRESHOLD_PX = 100;
const DISMISS_VELOCITY_PX_S = 300;

type Props = {
  notification: Notification;
  onDelete?: (id: string, e?: React.MouseEvent) => void;
  onClick?: (notification: Notification) => void;
  showDivider?: boolean;
};

export default function NotificationItem({
  notification,
  onDelete,
  onClick,
  showDivider,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const opacity = useTransform(
    x,
    [-200, -DISMISS_THRESHOLD_PX, 0, DISMISS_THRESHOLD_PX, 200],
    [0, 0.4, 1, 0.4, 0],
  );
  const isDismissing = useRef(false);
  const isDragging = useRef(false);
  const hasTriggeredFeedback = useRef(false);

  const avatarChildren = <NotificationsIcon sx={{ fontSize: 18 }} />;
  const avatarProps: { src?: string; children?: React.ReactNode } = {};
  if (notification.image) avatarProps.src = notification.image;
  else if (notification.icon) avatarProps.src = notification.icon;
  else avatarProps.children = avatarChildren;

  useMotionValueEvent(x, "change", (latest) => {
    if (isDismissing.current) return;
    const isPastThreshold = Math.abs(latest) >= DISMISS_THRESHOLD_PX;
    if (isPastThreshold && !hasTriggeredFeedback.current) {
      hasTriggeredFeedback.current = true;
      navigator.vibrate?.(40);
      void animate(y, [-8, 0], { duration: 0.3 });
    } else if (!isPastThreshold) {
      hasTriggeredFeedback.current = false;
    }
  });

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    hasTriggeredFeedback.current = false;
    // small delay to avoid the following click event firing after drag end
    setTimeout(() => (isDragging.current = false), 50);
    if (isDismissing.current) return;
    const xOffset = Math.abs(info.offset.x);
    const xVelocity = Math.abs(info.velocity.x);
    if (xOffset >= DISMISS_THRESHOLD_PX || xVelocity >= DISMISS_VELOCITY_PX_S) {
      isDismissing.current = true;
      const direction = info.offset.x >= 0 ? 1 : -1;
      animate(x, direction * 500, {
        type: "tween",
        duration: 0.2,
        ease: "easeOut",
        onComplete: () => {
          onDelete?.(notification.id);
        },
      });
    } else {
      animate(y, 0, { type: "spring", stiffness: 400, damping: 30 });
      animate(x, 0, { type: "spring", stiffness: 400, damping: 30 });
    }
  };

  return (
    <motion.div
      style={{ x, y, opacity }}
      drag={onDelete ? "x" : false}
      onDragStart={() => {
        isDragging.current = true;
      }}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.6}
      onDragEnd={handleDragEnd}
    >
      <ListItem
        component={motion.li}
        layout
        initial={false}
        transition={{ layout: { duration: 0.18, ease: "easeInOut" } }}
        disablePadding
        sx={{
          bgcolor: notification.isRead ? "transparent" : "action.hover",
          position: "relative",
          ...(showDivider && { borderBottom: 1, borderColor: "divider" }),
        }}
      >
        <ListItemButton
          component={motion.div}
          layout
          onClick={() => {
            if (isDragging.current) return;
            onClick?.(notification);
          }}
          sx={{ pr: { xs: "32px" }, py: 0.75 }}
        >
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, maxWidth: "100%" }}>
            <Avatar
              {...avatarProps}
              alt={notification.title}
              sx={{ width: 32, height: 32, mt: 1, flex: "0 0 auto" }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: notification.isRead ? 400 : 600,
                      lineHeight: 1.3,
                      display: "block",
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: expanded ? "normal" : "nowrap",
                    }}
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <Box
                    component="span"
                    sx={{ position: "relative", display: "block" }}
                  >
                    <MessageContainer
                      message={notification.message}
                      expanded={expanded}
                    />

                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        position: "relative",
                      }}
                    >
                      {notification.forAdmins && (
                        <Tooltip title="Notificare admin">
                          <AdminPanelSettingsIcon
                            sx={{
                              fontSize: 13,
                              color: "text.disabled",
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                      <AnimatePresence mode="wait">
                        {expanded ? (
                          <Box
                            key="smart"
                            component={motion.span}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 4 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{
                                color: "text.disabled"
                              }}
                            >
                              {formatSmartDate(notification.createdAt)}
                            </Typography>
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{
                                color: "text.disabled"
                              }}
                            >
                              ·
                            </Typography>
                            <DateTimeRelative
                              date={notification.createdAt}
                              variant="caption"
                              color="text.disabled"
                            />
                          </Box>
                        ) : (
                          <Box
                            key="relative"
                            component={motion.span}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <DateTimeRelative
                              date={notification.createdAt}
                              variant="caption"
                              color="text.disabled"
                            />
                          </Box>
                        )}
                      </AnimatePresence>
                    </Box>
                  </Box>
                }
              />
            </Box>
          </Box>
        </ListItemButton>

        {/* Expand toggle in top-right */}
        <Box sx={{ position: "absolute", top: 6, right: 6 }}>
          <Tooltip title={expanded ? "Restrânge" : "Extinde"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((s) => !s);
              }}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Delete button placed bottom-right, visible only when expanded */}
        <AnimatePresence>
          {expanded && onDelete && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              style={{ position: "absolute", right: 8, bottom: 8 }}
            >
              <Tooltip title="Șterge">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(notification.id, e);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
      </ListItem>
    </motion.div>
  );
}

function MessageContainer({
  message,
  expanded,
}: {
  message: string;
  expanded: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [fullHeight, setFullHeight] = useState<number>(0);
  const [lineHeightPx, setLineHeightPx] = useState<number>(18);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // measure full content height
      setFullHeight(el.scrollHeight);

      // compute line height
      const textEl = el.querySelector(".message-text") as Element | null;
      const computed = window.getComputedStyle(textEl ?? el);
      const lh = parseFloat(computed.lineHeight || "18") || 18;
      setLineHeightPx(lh);
    };

    // measure immediately (runs after DOM updates)
    measure();

    // re-measure on window resize in case wrapping changes
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [message, expanded]);

  const collapsedHeight = Math.round(lineHeightPx * 1); // one line
  const animateTo = expanded
    ? Math.max(fullHeight, collapsedHeight)
    : collapsedHeight;

  return (
    <motion.span
      style={{ display: "inline-block", overflow: "hidden", width: "100%" }}
      animate={{ height: animateTo }}
      initial={false}
      transition={{ height: { duration: 0.18, ease: "easeInOut" } }}
    >
      <span ref={ref} style={{ display: "block" }}>
        <Typography
          className="message-text"
          variant="caption"
          component="span"
          sx={{
            color: "text.secondary",
            whiteSpace: "pre-wrap",
            mt: 0.25,
            display: "block"
          }}>
          {expanded ? message : message.split("\n").join(" ")}
        </Typography>
      </span>
    </motion.span>
  );
}
